import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { Impact, SlaView, Urgency, WorkItemCard } from '../lib/types';
import { IMPACT_LABELS, SLA_STANDING_LABELS, URGENCY_LABELS, formatClock } from '../lib/types';
import { formatDateTime } from '../lib/dates';

interface Props {
  item: WorkItemCard;
  editable: boolean;
  onChanged: () => void | Promise<void>;
}

/**
 * Halo's two questions and the clock they produce. Impact and urgency are asked separately
 * and the matrix sets the priority, so nobody argues about "how urgent is urgent" — they
 * answer two concrete questions and the promise follows.
 */
export function SlaPanel({ item, editable, onChanged }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function set(patch: { impact?: Impact | null; urgency?: Urgency | null }) {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/work-items/${item.id}`, patch);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that');
    } finally {
      setBusy(false);
    }
  }

  async function markResponded() {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/work-items/${item.id}/responded`);
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record that');
    } finally {
      setBusy(false);
    }
  }

  const isService = !!item.impact && !!item.urgency;

  return (
    <div className="card mt-2">
      <div className="card-head">
        <h3>Service level</h3>
        {item.sla && <span className="dim" style={{ fontSize: 12 }}>{SLA_STANDING_LABELS[item.sla.standing]}</span>}
      </div>

      <div className="card-pad">
        {error && <div className="callout">{error}</div>}

        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          <label className="field grow" style={{ marginBottom: 0 }}>
            <span className="field-label">Impact — who is affected</span>
            <select
              value={item.impact ?? ''}
              disabled={!editable || busy}
              onChange={(e) => set({ impact: (e.target.value || null) as Impact | null })}
            >
              <option value="">Not service work</option>
              {(Object.keys(IMPACT_LABELS) as Impact[]).map((key) => (
                <option key={key} value={key}>
                  {IMPACT_LABELS[key]}
                </option>
              ))}
            </select>
          </label>

          <label className="field grow" style={{ marginBottom: 0 }}>
            <span className="field-label">Urgency — how badly</span>
            <select
              value={item.urgency ?? ''}
              disabled={!editable || busy}
              onChange={(e) => set({ urgency: (e.target.value || null) as Urgency | null })}
            >
              <option value="">Not service work</option>
              {(Object.keys(URGENCY_LABELS) as Urgency[]).map((key) => (
                <option key={key} value={key}>
                  {URGENCY_LABELS[key]}
                </option>
              ))}
            </select>
          </label>
        </div>

        {!isService ? (
          <p className="dim" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            Internal work, so no clock runs against it. Answer both questions to make this a
            service ticket with a reply and fix target.
          </p>
        ) : (
          <>
            <p className="dim" style={{ fontSize: 12, marginTop: 12, marginBottom: 12 }}>
              Priority <strong style={{ color: 'var(--text)' }}>{item.priority}</strong> comes from
              those two answers, not from a separate choice.
            </p>

            {item.sla && (
              <div className="stack-sm">
                {item.sla.paused && (
                  <div className="callout" style={{ marginBottom: 0 }}>
                    The clock is stopped while this waits on the customer
                    {item.sla.pausedSince ? `, since ${formatDateTime(item.sla.pausedSince)}` : ''}.
                  </div>
                )}

                <Target
                  label="Reply by"
                  target={item.sla.response}
                  answeredLabel="Answered in time"
                  action={
                    editable && item.sla.response && !item.sla.response.met && item.sla.response.remainingMinutes !== null ? (
                      <button type="button" className="btn btn-sm" disabled={busy} onClick={markResponded}>
                        We have replied
                      </button>
                    ) : null
                  }
                />
                <Target label="Fix by" target={item.sla.resolution} answeredLabel="Fixed in time" action={null} />

                {item.sla.pausedMinutes > 0 && (
                  <p className="dim" style={{ fontSize: 11, margin: 0 }}>
                    {formatClock(item.sla.pausedMinutes)} of waiting on the customer has been
                    excluded from both targets.
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Target({
  label,
  target,
  answeredLabel,
  action,
}: {
  label: string;
  target: SlaView['response'];
  answeredLabel: string;
  action: React.ReactNode;
}) {
  if (!target) return null;

  const answered = target.remainingMinutes === null;
  const tone = target.breached ? 'var(--accent)' : answered ? 'var(--ok)' : undefined;

  return (
    <div className="row-between">
      <span className="muted">{label}</span>
      <span className="row" style={{ gap: 8 }}>
        <span className="mono" style={{ color: tone }}>
          {new Date(target.due).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
        </span>
        {answered ? (
          <span className={target.breached ? 'badge badge-accent' : 'badge badge-ok'}>
            {target.breached ? 'Late' : answeredLabel}
          </span>
        ) : (
          <span className={target.breached ? 'badge badge-accent' : 'badge'}>
            {formatClock(target.remainingMinutes!)}
          </span>
        )}
        {action}
      </span>
    </div>
  );
}
